import {
  makeStyles,
  Popper,
  PopperPlacementType,
  Theme,
} from '@material-ui/core'
import classNames from 'classnames'
import React, { useRef } from 'react'

const useStyles = makeStyles((theme: Theme) => ({
  poperRoot: {
    zIndex: 999,
    '&[x-placement*="bottom"] $arrow': {
      top: 0,
      left: 0,
      marginTop: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '0 1em 1em 1em',
        borderColor: `transparent transparent ${theme.palette.divider} transparent`,
      },
    },
    '&[x-placement*="top"] $arrow': {
      bottom: 0,
      left: 0,
      marginBottom: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '1em 1em 0 1em',
        borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
      },
    },
    '&[x-placement*="right"] $arrow': {
      left: 0,
      marginLeft: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 1em 1em 0',
        borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
      },
    },
    '&[x-placement*="left"] $arrow': {
      right: 0,
      marginRight: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 0 1em 1em',
        borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
      },
    },
  },
  arrow: {
    position: 'absolute',
    fontSize: 7,
    width: '3em',
    height: '3em',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    },
  },
}))

type CustomPopperProps = {
  TooltipComponent: React.ReactElement
  AnchorComponent: React.ReactElement
  disableCloseOnBlur?: boolean
  isOpen?: boolean
  /**
   * If true, the popper will be rendered in a portal. Beware that the tab flow will be broken in case of a portal, since the next
   * element to be focused will be the browser navigation bar.
   * It's a hack since sticky elements (bomItem headers) are redenred on top of the tooltip content if it is not in a portal, so it's used
   * only in the project header
   */
  usePortal?: boolean
  placement?: PopperPlacementType
}

export const CustomPopper = ({
  TooltipComponent,
  AnchorComponent,
  disableCloseOnBlur,
  isOpen,
  usePortal,
  placement,
}: CustomPopperProps) => {
  const cn = useStyles()
  const [open, setOpen] = React.useState(isOpen ?? false)

  const divRef = React.useRef(null)
  const [arrowRef, setArrowRef] = React.useState(null)

  const closeTimeout = useRef<NodeJS.Timeout>()

  const handleClose = () => {
    closeTimeout.current = setTimeout(() => {
      setOpen(false)
    }, 200)
  }

  const cancelClose = () => clearTimeout(closeTimeout.current)

  return (
    <div
      onMouseLeave={handleClose}
      ref={divRef}
      onBlur={() => !disableCloseOnBlur && handleClose()}
      onFocus={() => setOpen(true)}
      onMouseEnter={() => setOpen(true)}
    >
      {AnchorComponent}
      <Popper
        open={isOpen || open}
        anchorEl={divRef.current}
        disablePortal={usePortal !== null ? !usePortal : true}
        className={cn.poperRoot}
        placement={placement || 'bottom'}
        onMouseLeave={handleClose}
        onMouseEnter={cancelClose}
        onFocus={cancelClose}
        modifiers={{
          flip: {
            enabled: true,
          },
          arrow: {
            enabled: true,
            element: arrowRef,
          },
          offset: {
            offset: '0 8,0',
          },
        }}
        transition
      >
        <span
          ref={setArrowRef}
          className={classNames('MuiPopper-arrow', cn.arrow)}
        ></span>
        {TooltipComponent}
      </Popper>
    </div>
  )
}
